import { getAudioContext } from './Context';
const createBuffer = require('audio-buffer-from');
const toWav = require('audiobuffer-to-wav');

const clamp = (value: number, min: number, max: number) => {
  return min < max
    ? value < min
      ? min
      : value > max
      ? max
      : value
    : value < max
    ? max
    : value > min
    ? min
    : value;
};

const isNeg = (val: number) => {
  return val === 0 && 1 / val === -Infinity;
};

const nidx = (idx: number, length: number) => {
  return idx == null
    ? 0
    : isNeg(idx)
    ? length
    : idx <= -length
    ? 0
    : idx < 0
    ? length + (idx % length)
    : Math.min(length, idx);
};

const fill = (
  buffer: AudioBuffer,
  value: Function,
  start: number,
  end: number
) => {
  let target = buffer;

  start = start == null ? 0 : nidx(start, buffer.length);
  end = end == null ? buffer.length : nidx(end, buffer.length);
  for (var channel = 0, c = buffer.numberOfChannels; channel < c; channel++) {
    var data = buffer.getChannelData(channel),
      targetData = target.getChannelData(channel);
    for (var i = start; i < end; i++) {
      targetData[i] = value.call(buffer, data[i], i, channel, data);
    }
  }

  return target;
};

const create = (src: Float32Array[], options: any, sampleRate: number) => {
  if (typeof options === 'number') {
    options = { channels: options };
  } else if (typeof options === 'string') {
    options = { format: options };
  } else if (!options) {
    options = {};
  }
  if (sampleRate) {
    options.sampleRate = sampleRate;
  }
  options.context = getAudioContext();

  return createBuffer(src, options);
};

export const sliceBuffer = (
  buffer: AudioBuffer,
  start: number,
  end: number
) => {
  start = start == null ? 0 : nidx(start, buffer.length);
  end = end == null ? buffer.length : nidx(end, buffer.length);

  var data: Float32Array[] = [];
  for (var channel = 0; channel < buffer.numberOfChannels; channel++) {
    var channelData = buffer.getChannelData(channel);
    data.push(channelData.slice(start, end));
  }
  return create(data, buffer.numberOfChannels, buffer.sampleRate);
};

export const trimBuffer = (buffer: AudioBuffer, level: number = 0) => {
  level = Math.abs(level);

  var channel, c, start, end, i;
  var data: Float32Array;

  start = buffer.length;
  // FIXME: replace with indexOF
  for (channel = 0, c = buffer.numberOfChannels; channel < c; channel++) {
    data = buffer.getChannelData(channel);
    for (i = 0; i < data.length; i++) {
      if (i > start) {
        break;
      }
      if (Math.abs(data[i]) > level) {
        start = i;
        break;
      }
    }
  }

  end = 0;
  // FIXME: replace with lastIndexOf
  for (channel = 0, c = buffer.numberOfChannels; channel < c; channel++) {
    data = buffer.getChannelData(channel);
    for (i = data.length - 1; i >= 0; i--) {
      if (i < end) {
        break;
      }
      if (Math.abs(data[i]) > level) {
        end = i + 1;
        break;
      }
    }
  }

  return sliceBuffer(buffer, start, end);
};

export const normalizeBuffer = (
  buffer: AudioBuffer,
  start: number = 0,
  end: number | null = null
) => {
  start = start == null ? 0 : nidx(start, buffer.length);
  end = end == null ? buffer.length : nidx(end, buffer.length);

  // for every channel bring it to max-min amplitude range
  var max = 0;

  for (var c = 0; c < buffer.numberOfChannels; c++) {
    var data = buffer.getChannelData(c);
    for (var i = start; i < end; i++) {
      max = Math.max(Math.abs(data[i]), max);
    }
  }

  var amp = Math.max(1 / max, 1);

  return fill(
    buffer,
    function (value: number) {
      return clamp(value * amp, -1, 1);
    },
    start,
    end
  );
};

export const getAudioBuffer = (file: File): Promise<AudioBuffer> => {
  return new Promise(
    (resolve: (buffer: AudioBuffer) => void, reject: Function) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Import callback function
        // provides PCM audio data decoded as an audio buffer
        getAudioContext().decodeAudioData(
          reader.result as ArrayBuffer,
          (buffer: AudioBuffer) => {
            resolve(buffer);
          },
          (err: any) => {
            console.log('Error with decoding audio data' + err.err);
            reject(err);
          }
        );
      };
      reader.readAsArrayBuffer(file);
    }
  );
};

export const videoHasAudio = (video: HTMLVideoElement): boolean => {
  const v = video as any;
  return (
    v.mozHasAudio ||
    Boolean(v.webkitAudioDecodedByteCount) ||
    Boolean(v.audioTracks && v.audioTracks.length)
  );
};

export const extractAudioFileFromVideo = (
  video: File
): Promise<File | null> => {
  return new Promise(resolve => {
    const audioContext = getAudioContext();
    const reader = new FileReader();
    reader.onload = async () => {
      const videoFileAsBuffer = reader.result as ArrayBuffer; // arraybuffer
      try {
        const audioBuffer = await audioContext.decodeAudioData(
          videoFileAsBuffer
        );
        const wav = toWav(audioBuffer);
        const file = new File([wav], video.name + '.wav');
        resolve(file);
      } catch {
        resolve(null);
      }
    };
    reader.readAsArrayBuffer(video); // video file
  });
};



// WEBPACK FOOTER //
// ./src/audio/AudioUtils.ts